import { memo } from 'react';
import clsx from 'clsx';

import styled from '@emotion/styled';
import { TableBody, TableCell, TableRow, Tooltip } from '@kc-console/common-ui';

import DataTableColumnVirtualizationSpacerCell from './DataTableColumnVirtualizationSpacerCell';
import DataTableEmptyState from './DataTableEmptyState';
import DataTableTableBodyCell from './DataTableTableBodyCell';
import DataTableTableBodyExpandCell from './DataTableTableBodyExpandCell';
import DataTableTableBodySelectionCell from './DataTableTableBodySelectionCell';
import { createBuiltinColumnVariableKeys, dataTableClasses } from './utils';

import type { JSX, ReactNode } from 'react';
import type { RowData, RowSelectionState, Updater } from '@tanstack/react-table';
import type { TextEllipsisState } from '../../hooks/DataTable/features';
import type { EmptyStateProps, EmptyStateType } from '../EmptyState';
import type { DataTableEmptyStateProps } from './DataTableEmptyState';
import type {
  DataTableCellContent,
  DataTableColumnVirtualizationState,
  DataTableRowContent,
} from './types';

export interface DataTableTableBodyProps<TData extends RowData> {
  contextMenuEnabled: boolean;
  emptyState:
    | Partial<Record<EmptyStateType, Omit<EmptyStateProps, 'type' | 'onRefetch'>>>
    | undefined;
  emptyStateType: EmptyStateType | null;
  hasAutoPadColumn: boolean;
  hasExpandColumn: boolean;
  hasSelectionColumn: boolean;
  hasSelectionColumnAsCheckbox: boolean;
  hasSelectionColumnAsRadio: boolean;
  headerCount: number;
  onRefetch: () => void;
  onRowClick: (e: React.MouseEvent<HTMLTableRowElement>, rowId: string) => void;
  onRowContextMenu: (e: React.MouseEvent<HTMLTableRowElement>, rowId: string) => void;
  onRowExpandingToggle: (rowId: string) => void;
  onRowSelectionChange: (updater: Updater<RowSelectionState>) => void;
  onRowSelectionToggle: (rowId: string) => void;
  renderFallbackValue: string;
  rows: DataTableRowContent<TData>[];
  tableContainerWidth: number | null;
  textEllipsis: TextEllipsisState;
  columnVirtualization?: DataTableColumnVirtualizationState;
  disabledExpandTooltip?: string;
  disabledRowTooltip?: string;
  emptyStateProps?: Pick<DataTableEmptyStateProps, 'containerStyle'>;
  renderExpandedRow?: (params: { open: boolean; rowId: string }) => ReactNode;
}

export default memo(function DataTableTableBody<TData extends RowData>(
  props: DataTableTableBodyProps<TData>,
) {
  const {
    columnVirtualization,
    contextMenuEnabled,
    disabledExpandTooltip,
    disabledRowTooltip,
    emptyStateType,
    emptyState,
    onRefetch,
    rows,
    hasAutoPadColumn,
    hasExpandColumn,
    hasSelectionColumn,
    emptyStateProps,
    hasSelectionColumnAsCheckbox,
    hasSelectionColumnAsRadio,
    headerCount,
    onRowClick,
    onRowContextMenu,
    onRowExpandingToggle,
    onRowSelectionChange,
    onRowSelectionToggle,
    renderExpandedRow,
    renderFallbackValue,
    tableContainerWidth,
    textEllipsis,
  } = props;

  const renderCell = (cell: DataTableCellContent<TData, unknown>) => (
    <DataTableTableBodyCell
      key={cell.id}
      cell={cell}
      renderFallbackValue={renderFallbackValue}
      textEllipsis={textEllipsis}
    />
  );

  return (
    <TableBody>
      {emptyStateType ? (
        <DataTableEmptyState
          type={emptyStateType}
          colSpan={headerCount}
          onRefetch={onRefetch}
          tableContainerWidth={tableContainerWidth}
          {...emptyStateProps}
          {...emptyState?.[emptyStateType]}
        />
      ) : (
        rows.map(row => {
          const {
            id: rowId,
            cells,
            canExpand,
            depth,
            expanded,
            expandingHidden,
            rowInteractionDisabled,
            selected,
            selectionDisabled,
            selectionHidden,
          } = row;
          const tooltipMessage =
            rowInteractionDisabled && disabledRowTooltip ? disabledRowTooltip : undefined;
          // 확장 불가(canExpand=false)인 행에서만 확장 버튼 hover 시 사유 툴팁을 노출한다.
          const expandDisabledMessage =
            !canExpand && disabledExpandTooltip ? disabledExpandTooltip : undefined;

          const {
            left: leftCells,
            center: centerCells,
            right: rightCells,
          } = cells.reduce(
            (result, cell) => {
              result[cell.stickyPosition || 'center'].push(cell);
              return result;
            },
            { left: [], center: [], right: [] } as Record<
              'left' | 'center' | 'right',
              DataTableCellContent<TData, unknown>[]
            >,
          );

          return (
            <Tooltip key={rowId} message={tooltipMessage} placement="top">
              <TableRow
                className={clsx(
                  rowInteractionDisabled && dataTableClasses.disabledRow,
                  hasSelectionColumn &&
                    !selectionDisabled &&
                    !selectionHidden &&
                    dataTableClasses.selectableRow,
                )}
                onClick={e => onRowClick(e, rowId)}
                selected={selected}
                hover={hasSelectionColumn}
                subRow={depth > 0}
                collapse={canExpand && !!renderExpandedRow}
                open={expanded}
                collapseProps={{
                  colSpan: headerCount,
                  renderRow: open => renderExpandedRow?.({ open, rowId }) ?? null,
                }}
                onContextMenu={contextMenuEnabled ? e => onRowContextMenu(e, rowId) : undefined}
              >
                <DataTableTableBodyExpandCell
                  canExpand={canExpand}
                  depth={depth}
                  expanded={expanded}
                  expandingHidden={expandingHidden}
                  hasExpandColumn={hasExpandColumn}
                  message={expandDisabledMessage}
                  onRowExpandingToggle={onRowExpandingToggle}
                  rowId={rowId}
                />

                <DataTableTableBodySelectionCell
                  hasSelectionColumn={hasSelectionColumn}
                  hasSelectionColumnAsCheckbox={hasSelectionColumnAsCheckbox}
                  hasSelectionColumnAsRadio={hasSelectionColumnAsRadio}
                  onRowSelectionChange={onRowSelectionChange}
                  onRowSelectionToggle={onRowSelectionToggle}
                  rowId={rowId}
                  selected={selected}
                  selectionDisabled={selectionDisabled}
                  selectionHidden={selectionHidden}
                />

                {leftCells.map(renderCell)}
                {columnVirtualization?.leftSpacer && (
                  <DataTableColumnVirtualizationSpacerCell
                    spacer={columnVirtualization.leftSpacer}
                  />
                )}
                {centerCells.map(renderCell)}
                {columnVirtualization?.rightSpacer && (
                  <DataTableColumnVirtualizationSpacerCell
                    spacer={columnVirtualization.rightSpacer}
                  />
                )}
                {hasAutoPadColumn && <AutoPadRightBodyCell />}
                {rightCells.map(renderCell)}
              </TableRow>
            </Tooltip>
          );
        })
      )}
    </TableBody>
  );
}) as <TData extends RowData>(props: DataTableTableBodyProps<TData>) => JSX.Element;

const AutoPadRightBodyCell = styled(TableCell)`
  && {
    padding: 0;
    width: var(${createBuiltinColumnVariableKeys('autoPadRight').size});
  }
`;
